import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { Rotate } from '@components/animators/Rotate';
import { Button } from '@components/button';
import { EmptyState } from '@components/empty-state';
import { COLOR_TO_VARIANT, ICON_CHOOSER_ICONS } from '@components/icon-chooser/icon-chooser';
import { IconFancyContainer } from '@components/icon-fancy-container';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconDropdown } from '@components/icons/dropdown';
import { IconDropRight } from '@components/icons/dropright';
import { IconDropside } from '@components/icons/dropside';
import { IconForm } from '@components/icons/form';
import { ListCard } from '@components/list-card';
import { SearchBox } from '@components/search-box';
import { hideSidebar, Sidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { lineClamp } from '@styles/layout-helpers';
import { Color, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { useAddJourneyFormToPortal, useWorkspaceJourneyForms } from 'api/portals/hooks';
import { WorkspaceWithJourneyForms } from 'api/portals/requests';
import { useWorkspaces } from 'api/workspaces/hooks';
import { Workspace, WorkspaceIconName } from 'api/workspaces/requests';

import { useAddToPortalSidebar } from './add-to-portal-sidebar';
import { useWorkspaceSearch } from './use-workspace-search';

export const useAddJourneyFormSidebar = () => {
  const sidebar = useSidebar(AddJourneyFormSidebar, {
    variant: 'REGULAR',
    closeOnClickOutside: true,
  });
  return sidebar;
};

export const AddJourneyFormSidebar = ({ portalShortId }: { portalShortId: string }) => {
  const { t } = useScopedTranslate(locale);
  const { data: workspaceJourneyForms, isLoading } = useWorkspaceJourneyForms(portalShortId);
  const { data: workspaces } = useWorkspaces();
  const addJourneyFormToPortal = useAddJourneyFormToPortal();
  const showAddToPortalSidebar = useAddToPortalSidebar();

  const {
    setSearchStr,
    visibleWorkspaces,
    collapsedState,
    expandCollapseText,
    handleExpandCollapse,
    toggleCollapse,
  } = useWorkspaceSearch(workspaceJourneyForms ?? []);

  const getAreAllWorkspacesEmpty = (workspaces: typeof workspaceJourneyForms) => {
    if (!workspaces || workspaces.length === 0) {
      return true;
    }
    return workspaces.every((workspace) => !workspace.forms || workspace.forms.length === 0);
  };

  if (isLoading) {
    return <Sidebar.Spinner />;
  }

  const handleAdd = (form: { shortId: string }) => {
    addJourneyFormToPortal.mutate({ form: form.shortId, portalShortId });
  };

  const areAllWorkspacesEmpty = getAreAllWorkspacesEmpty(workspaceJourneyForms);

  const getWorkspaceData = (workspaceId: string) => {
    const workspace = workspaces?.find((workspace: Workspace) => workspace.shortId === workspaceId);
    return {
      icon: workspace?.icon ?? 'IconWorkspace',
      color: (workspace?.color ?? 'green100') as Color,
      title: workspace?.title ?? '',
    };
  };

  return (
    <Sidebar>
      <Sidebar.Header>
        <Stack space={4}>
          <Stack
            orientation="horizontal"
            justify="space-between"
            align="center"
            style={{ padding: `${pxToRem(8)} 0` }}
          >
            <Stack space={2} orientation="horizontal" align="center">
              <IconArrowLeft
                color="buttonIconPositive"
                style={{ cursor: 'pointer' }}
                onClick={() => showAddToPortalSidebar({ portalShortId })}
              />
              <Sidebar.HeaderTitle title={t('selectJourneyFormToAdd')} />
            </Stack>
            <IconCloseRegular color="buttonIconPositive" onClick={hideSidebar} />
          </Stack>
          <Stack space={2} orientation="horizontal">
            <div style={{ flexGrow: 1 }}>
              <SearchBox onChange={setSearchStr} placeholder={t('core.search')} />
            </div>
            <Button
              variant="secondary"
              iconRight={
                expandCollapseText === t('expandAll') ? <IconDropRight /> : <IconDropdown />
              }
              text={expandCollapseText}
              onClick={handleExpandCollapse}
              isDisabled={visibleWorkspaces.length === 0}
            />
          </Stack>
        </Stack>
      </Sidebar.Header>
      <Sidebar.Content>
        <Stack space={2}>
          {areAllWorkspacesEmpty && (
            <EmptyState
              header={t('noExternalForms')}
              content={t('emptyStateDescription')}
              icon={<IconForm />}
              variant="small"
              skipMargin
            />
          )}
          {visibleWorkspaces?.map((workspace) => {
            const workspaceData = getWorkspaceData(workspace.shortId);
            return (
              <WorkspaceGroup
                key={workspace.shortId}
                workspace={{ ...workspace, ...workspaceData }}
                isCollapsed={collapsedState[workspace.shortId]}
                onToggle={() => toggleCollapse(workspace.shortId)}
                onAddForm={handleAdd}
              />
            );
          })}
        </Stack>
      </Sidebar.Content>
    </Sidebar>
  );
};

const WorkspaceGroup = ({
  workspace,
  isCollapsed,
  onToggle,
  onAddForm,
}: {
  workspace: WorkspaceWithJourneyForms & {
    icon: WorkspaceIconName;
    color: Color;
  };
  isCollapsed: boolean;
  onToggle: () => void;
  onAddForm: (form: { shortId: string }) => void;
}) => {
  const { t } = useScopedTranslate(locale);
  const IconComponent = ICON_CHOOSER_ICONS[workspace.icon] ?? ICON_CHOOSER_ICONS.IconAI;

  return (
    <Stack space={2}>
      <WorkspaceHeader onClick={onToggle}>
        <Stack orientation="horizontal" space={2} align="center">
          <IconFancyContainer
            icon={<IconComponent color={workspace.color} size={16} />}
            variant={COLOR_TO_VARIANT[workspace.color]}
            isSmall
          />
          <Text text={workspace.title} font="semibold14" />
          <Rotate isRotated={!isCollapsed}>
            <IconDropside color="buttonIconPositive" />
          </Rotate>
        </Stack>
      </WorkspaceHeader>
      <Collapse isCollapsed={isCollapsed}>
        {workspace.forms.length === 0 ? (
          <Text
            text={t('noExternalForms')}
            font="regular12"
            color="textHelper"
            style={{ padding: `0 ${pxToRem(8)}` }}
          />
        ) : (
          <Stack space={1}>
            {workspace.forms.map((form) => (
              <ListCard
                key={form.shortId}
                title={form.name}
                description={
                  <Stack>
                    <Text
                      font="regular12"
                      color="textSecondary"
                      text={form.portalDescription}
                      style={lineClamp('-webkit-box', 1, true)}
                    />
                    <Text
                      font="regular12"
                      color="textSecondary"
                      text={form.journeyName}
                      style={lineClamp('-webkit-box', 1, true)}
                    />
                  </Stack>
                }
                iconRight={
                  <Button
                    text={t('core.add')}
                    iconLeft={<IconAddPlus color="buttonIconPositive" />}
                    variant="ghost"
                    size="sm"
                    onClick={() => onAddForm(form)}
                  />
                }
                spaceX={4}
                spaceY={2}
              />
            ))}
          </Stack>
        )}
      </Collapse>
    </Stack>
  );
};

const WorkspaceHeader = styled.button({
  borderRadius: pxToRem(4),
  paddingLeft: pxToRem(8),
  paddingRight: pxToRem(8),
  minHeight: pxToRem(40),
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  ':hover': {
    backgroundColor: theme.color.uiTrans5,
  },
});

export const locale = {
  en: {
    selectJourneyFormToAdd: 'Select external form to add',
    noExternalForms: 'No external forms',
    emptyStateDescription:
      'External forms created in the Journey Builder will appear here, ready to be added to your Portal',
    expandAll: 'Expand all',
    collapseAll: 'Collapse all',
  },
};
